import { FC } from "react";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { EllipsisHorizontalIcon, TrashIcon } from "@heroicons/react/20/solid";
import { cn } from "helpers/classNames";

export interface ActionMenuItem {
  label: string;
  onClick: () => void;
  icon?: React.ReactNode;
  isHidden?: boolean;
  isDelete?: boolean;
}

interface Props {
  items: ActionMenuItem[];
  isSmall?: boolean;
  isDiscreete?: boolean;
  className?: string;
}

export const DotMenu: FC<Props> = ({
  items,
  isSmall,
  isDiscreete,
  className,
}) => {
  const visibleItems = items.filter(({ isHidden }) => !isHidden);
  const hasAnyIcon = visibleItems.some(
    ({ icon, isDelete }) => icon || isDelete,
  );

  return (
    <Menu as="div" className={cn("relative", className)}>
      <MenuButton
        className={cn(
          "-m-2 flex items-center rounded-full p-0.5 outline-none",
          isDiscreete
            ? "border-2 border-transparent text-gray-300 hover:text-gray-500"
            : "border-2 border-gray-200 text-gray-500 hover:text-gray-600",
        )}
      >
        <span className="sr-only">Open options</span>
        <EllipsisHorizontalIcon
          className={cn(isSmall ? "h-3.5 w-3.5" : "h-5 w-5")}
          aria-hidden="true"
        />
      </MenuButton>

      <MenuItems
        anchor="bottom end"
        className="z-50 mt-2 origin-top-right rounded-md border border-gray-75 bg-white py-2 shadow-lg ring-1 ring-gray-900/5 focus:outline-none"
      >
        {visibleItems.map(({ label, onClick, icon, isDelete }) => (
          <MenuItem key={label}>
            <button
              className={cn(
                "group flex w-full items-center px-4 py-2 text-sm",
                isDelete
                  ? "text-red-600 data-[focus]:bg-red-50 data-[focus]:text-red-700"
                  : "text-gray-700 data-[focus]:bg-gray-50 data-[focus]:text-gray-900",
              )}
              onClick={(e) => {
                e.stopPropagation();
                onClick();
              }}
            >
              {isDelete ? (
                <TrashIcon className="mr-3 h-5 w-5 text-red-400 group-hover:text-red-500" />
              ) : icon ? (
                <span className="mr-3 flex h-5 w-5 items-center justify-center">
                  {icon}
                </span>
              ) : (
                hasAnyIcon && (
                  <span className="mr-3 h-5 w-5" aria-hidden="true" />
                )
              )}
              {label}
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
};
